import React, { useState } from "react";
import {
  Checkbox,
  Form,
  Input,
  Button,
  Card,
  Typography,
  message,
  Steps,
  Select,
  Space,
} from "antd";
import { ArrowLeftOutlined, LockOutlined, MailOutlined, UserOutlined, ShopOutlined } from "@ant-design/icons";
import { Plane } from "lucide-react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../store";
import { registerUser } from "../store/authSlice";
import { Link, useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const { Step } = Steps;
const { Option } = Select;

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [messageApi, contextHolder] = message.useMessage();

  const [step, setStep] = useState<number>(0);

  // Business
  const [businessName, setBusinessName] = useState("");
  const [businessType, setBusinessType] = useState<"salon" | "barbershop" | "both">("salon");
  const [businessAddress, setBusinessAddress] = useState("");

  // Owner / Account
  const [ownerName, setOwnerName] = useState("");
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const submitBusiness = async () => {
    if (!businessName.trim()) return messageApi.error("Please enter your business name");
    if (!businessAddress.trim()) return messageApi.error("Please enter your business address");
    setStep(1);
  };

  const submitAccount = async () => {
    if (!ownerName.trim()) return messageApi.error("Please enter your name");
    if (!emailOrPhone.trim()) return messageApi.error("Please enter email or phone");
    if (!password) return messageApi.error("Please enter a password");
    if (password.length < 8) return messageApi.error("Password must be at least 8 characters");
    if (password !== confirmPassword) return messageApi.error("Passwords do not match");
    if (!agreedToTerms) return messageApi.error("Please agree to the Terms of Service and Privacy Policy");

    // Register user via existing redux thunk
    setIsLoading(true);
    try {
      const payload = {
        email: emailOrPhone.includes("@") ? emailOrPhone : undefined,
        phone: emailOrPhone.includes("@") ? undefined : emailOrPhone,
        name: ownerName,
        password,
        businessName,
        businessAddress,
        businessType,
      } as any;

      await dispatch(registerUser(payload)).unwrap();
      messageApi.success("Verification code sent!");
      navigate("/verify", { state: { email: payload.email || payload.phone } });
    } catch (err: any) {
      messageApi.error(err || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      <div style={{ minHeight: "100vh", background: "linear-gradient(135deg,#f0f7ff 0%,#fff 50%,#faf5ff 100%)" }}>
        <div style={{ padding: 24 }}>
          <div style={{ maxWidth: 980, margin: "0 auto" }}>
            <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, width: "fit-content" }}>
              <div style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                background: "linear-gradient(135deg,#3b82f6,#8b5cf6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                transform: "rotate(45deg)",
              }}>
                <Plane size={22} />
              </div>
              <Title level={4} style={{ margin: 0, color: "inherit" }}>
                Hairplane
              </Title>
            </Link>

            <Card style={{ borderRadius: 16, boxShadow: "0 10px 30px rgba(16,24,40,0.06)" }}>
              <div style={{ display: "flex", gap: 32, alignItems: "flex-start", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 320 }}>
                  <Steps current={step} direction="vertical" responsive={false}>
                    <Step title="Business Info" description="Tell us about your salon or barbershop" />
                    <Step title="Account Info" description="Create login credentials" />
                    <Step title="Verification" description="Verify your account" />
                  </Steps>
                </div>

                <div style={{ flex: 2, minWidth: 320 }}>
                  {step !== 0 && (
                    <Button type="link" icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ padding: 0 }}>
                      Back
                    </Button>
                  )}

                  {step === 0 && (
                    <div>
                      <div style={{ textAlign: "center", marginBottom: 18 }}>
                        <div style={{ width: 64, height: 64, margin: "0 auto 12px", borderRadius: 14, background: "linear-gradient(135deg,#3b82f6,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", color: "white" }}>
                          <ShopOutlined />
                        </div>
                        <Title level={3}>Start Your Free Trial</Title>
                        <Text type="secondary">Tell us about your business</Text>
                      </div>

                      <Form layout="vertical" onFinish={submitBusiness}>
                        <Form.Item label="Business Name" required>
                          <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Elite Cuts Barbershop" prefix={<UserOutlined />} />
                        </Form.Item>
                        <Form.Item label="Business Type" required>
                          <Select value={businessType} onChange={(v) => setBusinessType(v)}>
                            <Option value="salon">Hair Salon</Option>
                            <Option value="barbershop">Barbershop</Option>
                            <Option value="both">Both</Option>
                          </Select>
                        </Form.Item>
                        <Form.Item label="Business Address" required>
                          <Input value={businessAddress} onChange={(e) => setBusinessAddress(e.target.value)} placeholder="123 Main St" />
                        </Form.Item>

                        <Button type="primary" block onClick={submitBusiness}>
                          Continue
                        </Button>
                      </Form>
                    </div>
                  )}

                  {step === 1 && (
                    <div>
                      <div style={{ textAlign: "center", marginBottom: 18 }}>
                        <div style={{ width: 64, height: 64, margin: "0 auto 12px", borderRadius: 14, background: "linear-gradient(135deg,#8b5cf6,#ec4899)", display: "flex", alignItems: "center", justifyContent: "center", color: "white" }}>
                          <LockOutlined />
                        </div>
                        <Title level={3}>Create Your Account</Title>
                        <Text type="secondary">Set up your login credentials</Text>
                      </div>

                      <Form layout="vertical" onFinish={submitAccount}>
                        <Form.Item label="Your Name" required>
                          <Input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} prefix={<UserOutlined />} />
                        </Form.Item>

                        <Form.Item label="Email or Phone" required>
                          <Input value={emailOrPhone} onChange={(e) => setEmailOrPhone(e.target.value)} prefix={<MailOutlined />} placeholder="you@example.com or (555) 123-4567" />
                        </Form.Item>

                        <Form.Item label="Password" required>
                          <Input.Password value={password} onChange={(e) => setPassword(e.target.value)} prefix={<LockOutlined />} />
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>Must be at least 8 characters long</div>
                        </Form.Item>

                        <Form.Item label="Confirm Password" required>
                          <Input.Password value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} prefix={<LockOutlined />} />
                        </Form.Item>

                        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13 }}>
                          <Checkbox checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)}>
                            <span style={{ color: "#6b7280" }}>
                              I agree to Hairplane's{" "}
                              <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
                              {" "}and{" "}
                              <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
                            </span>
                          </Checkbox>
                        </div>

                        <Space direction="vertical" style={{ width: "100%" }}>
                          <Button type="default" block onClick={() => setStep(0)}>
                            Edit Business Info
                          </Button>
                          <Button type="primary" block loading={isLoading} disabled={!agreedToTerms} onClick={submitAccount}>
                            {isLoading ? "Sending Code..." : "Continue"}
                          </Button>
                        </Space>
                      </Form>
                    </div>
                  )}

                </div>
              </div>
            </Card>

            <div style={{ textAlign: "center", marginTop: 16 }}>
              <Text type="secondary">
                Already have an account? <Link to="/login">Sign in</Link>
              </Text>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RegisterPage;